/**
 * Bonus Calculator — determines Wouter's monthly bonus progress.
 *
 * Bonus tiers (thresholds lowered 5 November 2026, CLAUDE.md §10.1):
 *   Bronze (15 files) — R1,000
 *   Silver (20 files) — PAYE equivalent (configurable)
 *   Gold   (25 files) — Bronze + Silver combined
 *
 * The live thresholds come from the `settings` table (keys
 * bonus_bronze_count / bonus_silver_count / bonus_gold_count, edited in
 * Settings → Bonus Configuration). BONUS_THRESHOLD_DEFAULTS below is the
 * ONE fallback set in the codebase, used only when a setting is missing
 * or zero; settingsApi.js and Settings.jsx import it rather than
 * repeating the numbers, and the tier badges in useAchievements.js read
 * the resolved milestones from calculateBonusStatus(), so the tracker,
 * the badges, the weekly emails and the Settings form can never disagree.
 */

export const BONUS_THRESHOLD_DEFAULTS = Object.freeze({
  bronze: 15,
  silver: 20,
  gold: 25,
});

const DEFAULT_MILESTONES = {
  bronze: { count: BONUS_THRESHOLD_DEFAULTS.bronze, amount: 1000, label: 'Bronze', emoji: '🥉' },
  silver: { count: BONUS_THRESHOLD_DEFAULTS.silver, amount: 0, label: 'Silver', emoji: '🥈' },
  gold: { count: BONUS_THRESHOLD_DEFAULTS.gold, amount: 0, label: 'Gold', emoji: '🥇' },
};

/**
 * Calculate bonus status for a given count of qualifying files.
 *
 * @param {number} qualifyingCount — number of bonus-qualifying files this month
 * @param {object} settings — optional overrides for milestones and PAYE amount
 * @returns {object} bonus status details
 */
export function calculateBonusStatus(qualifyingCount, settings = {}) {
  const milestones = {
    bronze: {
      ...DEFAULT_MILESTONES.bronze,
      count: settings.bronzeTarget || DEFAULT_MILESTONES.bronze.count,
      amount: settings.bronzeAmount ?? DEFAULT_MILESTONES.bronze.amount,
    },
    silver: {
      ...DEFAULT_MILESTONES.silver,
      count: settings.silverTarget || DEFAULT_MILESTONES.silver.count,
      amount: settings.silverAmount ?? DEFAULT_MILESTONES.silver.amount,
    },
    gold: {
      ...DEFAULT_MILESTONES.gold,
      count: settings.goldTarget || DEFAULT_MILESTONES.gold.count,
      amount: settings.goldAmount ?? DEFAULT_MILESTONES.gold.amount,
    },
  };

  // Determine current tier and next target
  let currentTier = null;
  let nextTier = milestones.bronze;

  if (qualifyingCount >= milestones.gold.count) {
    currentTier = milestones.gold;
    nextTier = null; // All tiers achieved
  } else if (qualifyingCount >= milestones.silver.count) {
    currentTier = milestones.silver;
    nextTier = milestones.gold;
  } else if (qualifyingCount >= milestones.bronze.count) {
    currentTier = milestones.bronze;
    nextTier = milestones.silver;
  }

  const progressToNext = nextTier
    ? Math.min(100, (qualifyingCount / nextTier.count) * 100)
    : 100;

  const filesUntilNext = nextTier ? Math.max(0, nextTier.count - qualifyingCount) : 0;

  return {
    qualifyingCount,
    currentTier,
    nextTier,
    progressToNext,
    filesUntilNext,
    milestones,
    totalEarned: calculateTotalEarned(qualifyingCount, milestones),
  };
}

/**
 * Calculate total bonus amount earned based on current count.
 */
function calculateTotalEarned(count, milestones) {
  if (count >= milestones.gold.count) return milestones.gold.amount;
  if (count >= milestones.silver.count) return milestones.silver.amount;
  if (count >= milestones.bronze.count) return milestones.bronze.amount;
  return 0;
}

/**
 * Format a Rand amount for display (e.g. "R1,000").
 */
export function formatRands(amount) {
  return `R${amount.toLocaleString('en-ZA')}`;
}
